// stores/useLogger.js
import { defineStore } from "pinia";
import services from '@/services/api'

const FLUSH_INTERVAL_MS = 3_000;
const MAX_BATCH_SIZE = 200;
const MAX_RETRIES = 3;

export const useLogger = defineStore("logger", {
  state: () => ({
    queue: [],
    timerId: null,
    sending: false,
    retries: 0, // start at 0
    endpoint: services.baseURL+"api/logs/batch/", // baseURL already includes /enodo/
    sessionId: crypto?.randomUUID?.() ?? String(Date.now()),
  }),

  actions: {
    log(
      event,
      {
        level = "info",
        category = null,
        metadata = null,
        page = typeof window !== "undefined" ? window.location.pathname : null,
        referrer = typeof document !== "undefined" ? document.referrer : null,
        source = "frontend",
        endpoint = null,
        http_method = null,
        status_code = null,
        duration_ms = null,
        error_message = null,
        error_stack = null,
        vuo_id = null,
        request_id = null,
        correlation_id = null,
        timestamp = null,
      } = {}
    ) {
      // LOGGER DISABLED FOR NOW
      return;

      const user_agent =
        typeof navigator !== "undefined" ? navigator.userAgent : null;

      const item = {
        vuo_id,
        session_id: this.sessionId,
        request_id,
        correlation_id,
        source,
        level,
        event,
        category,
        timestamp,
        endpoint,
        http_method,
        status_code,
        duration_ms,
        page,
        referrer,
        user_agent,
        // ip_address -> let backend infer from request
        error_message,
        error_stack,
        meta: metadata, // Pydantic alias maps to column "metadata"
      };

      this.queue.push(item);

      if (this.queue.length > 10_000) {
        this.queue.splice(0, this.queue.length - 10_000);
      }

      if (!this.timerId) {
        this.timerId = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
      }
    },

    async flush() {
      // LOGGER DISABLED FOR NOW
      return;

      if (this.sending || this.queue.length === 0) return;
      this.sending = true;

      const batch = this.queue.slice(0, MAX_BATCH_SIZE);
      const json = JSON.stringify(batch);
      const body = `d=${encodeURIComponent(json)}`; // <-- percent-encode

      try {
        const res = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
          body,
          // credentials: 'omit' // consider omitting cookies to avoid extra scrutiny unless needed
        });
        if (!res.ok) throw new Error(`Failed to send logs: ${res.status}`);
        this.queue.splice(0, batch.length);
        this.retries = 0;
      } catch (err) {
        console.error('[logger] flush failed', err);
        if (this.retries > MAX_RETRIES) {
          this.queue.splice(0, batch.length);
          this.retries = 0;
        }
      } finally {
        this.sending = false;
        if (this.queue.length === 0 && this.timerId) {
          clearInterval(this.timerId);
          this.timerId = null;
        }
      }
    },


    // Flush-on-close using sendBeacon (best effort)
    wireLifecycle() {
      if (typeof window === 'undefined') return;

      const doKeepalive = () => {
        if (this.queue.length === 0) return;
        const url = new URL(this.endpoint, services.apiClient.defaults.baseURL).toString();
        const body = `d=${encodeURIComponent(JSON.stringify(this.queue))}`;
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
          body,
          keepalive: true,
        });
        this.queue = [];
        this.retries = 0;
      };

      const onVis = () => {
        if (document.visibilityState === 'hidden') doKeepalive();
      };

      window.addEventListener('beforeunload', doKeepalive);
      document.addEventListener('visibilitychange', onVis);
    }
  },
});
